import { useCallback, useMemo, useState } from 'react';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { useSelector } from 'react-redux';
import i18n from 'i18n-js';
import {
  DETAILS_SCREEN_TAB_NAME,
  pluralTabNameMap,
  pluralTitleRegExp,
  SMART_FACTORY,
} from '../../../constants/base.constants';
import { injectConditionalObject, isPhoneNative, isWeb } from '@helpers';
import { MACHINES_MAIN_SCREEN } from '@v2/pages/machines/navigation/config';
import { MachinesScreen } from '@v2/pages/machines/common/enums';
import { setShouldHoverBatch } from '@reducers/batch';
import {
  clearHeaderConfig,
  goBackInPerspectiveTabHistory,
  selectEditingPartData,
  selectIsEditModeForPartSwitchOn,
  selectNcFileData,
  selectPerspectiveTabHistory,
  selectPrevPerspectiveNameValueHistory,
  selectTabHistory,
  setIsEditModeForPartSwitchOn,
} from '../../../../../../../reducers/details';
import { selectNonBottomSheetScreen } from '@reducers/v2/bottomSheet/bottomSheet.slice';
import { useHandleNavigationContextHeader } from '@v2/pages/common/hooks/useHandleNavigationContextHeader';
import { LevelSubScreen, ModalType } from '@v2/pages/common/enums';
import { addProductionInformationV2 } from '@reducers/manufacture';
import { InfoScreen } from '@interfaces/base';
import { useUploadNcFileVersion } from '@components/ncFileVersions/hooks/useUploadNcFileVersion';
import { useHandleMainDetailsScreenNavigation } from '@v2/pages/common/hooks/useHandleMainDetailsScreenNavigation';
import { useHandleBottomSheetActions } from '@v2/pages/common/hooks/useHandleBottomSheetActions';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { DetailsPageWrapperProps } from '@v2/pages/common/components/DetailsPageWrapper/DetailsPageWrapper';

export interface UseManageDetailsPageWrapperParams extends Pick<DetailsPageWrapperProps, 'title'> {}

export const useManageDetailsPageWrapper = ({ title }: UseManageDetailsPageWrapperParams) => {
  const dispatch = useAppDispatch();

  const perspectiveTabHistory = useSelector(selectPerspectiveTabHistory);
  const tabHistory = useSelector(selectTabHistory);
  const isEditModeForPartSwitchOn = useSelector(selectIsEditModeForPartSwitchOn);
  const editingPartData = useSelector(selectEditingPartData);
  const prevPerspectiveNameValueHistory = useSelector(selectPrevPerspectiveNameValueHistory);
  const ncFileData = useSelector(selectNcFileData);
  const nonBottomSheetScreen = useSelector(selectNonBottomSheetScreen);

  const navigation = useNavigation<any>();
  const [actionsModalOpen, setActionsModalOpen] = useState<boolean>(false);

  const { activeLevelSubScreenName } = useHandleNavigationContextHeader();
  const { navigateToMainDetailsScreen } = useHandleMainDetailsScreenNavigation();
  const { onUploadNewVersion } = useUploadNcFileVersion({ ncFile: ncFileData?.data });
  const { closeBottomSheet } = useHandleBottomSheetActions();

  useFocusEffect(
    useCallback(() => {
      if (isWeb) {
        navigation.setOptions({ title: `${title} - ${SMART_FACTORY}` });
      }
    }, [title]),
  );

  const prevActiveTab: InfoScreen = perspectiveTabHistory.at(-2); // For back button text, this will change only if perspective change
  const currentActiveTab: InfoScreen = tabHistory.at(-1); // For current screen title, should change every screen

  const shouldDisplayBackButton = perspectiveTabHistory.length > 1;

  const shouldCurrentTabUsePluralName = currentActiveTab && pluralTitleRegExp.test(currentActiveTab?.value);
  const shouldPrevTabUsePluralName = prevActiveTab && pluralTitleRegExp.test(prevActiveTab?.value);

  const activeTabTitle = useMemo(() => {
    if (!currentActiveTab || !currentActiveTab?.value) {
      return i18n.t(title);
    }

    const shouldHideName = isPhoneNative && currentActiveTab?.name !== 'Batch';

    return shouldCurrentTabUsePluralName
      ? `${currentActiveTab?.value.replace('#', '')} ${i18n.t(pluralTabNameMap?.[currentActiveTab?.name])}`
      : `${shouldHideName ? '' : i18n.t(currentActiveTab?.name) + ' '}${currentActiveTab?.value}`;
  }, [currentActiveTab?.name, currentActiveTab?.value, shouldCurrentTabUsePluralName]);

  const prevTabTitle = useMemo(() => {
    if (!prevActiveTab || !prevActiveTab?.value) {
      return ' ';
    }

    const getTabName = () => {
      if (!prevActiveTab) {
        return '';
      }

      if (shouldPrevTabUsePluralName) {
        const baseValue = prevActiveTab?.value?.replace('#', '');
        const pluralName = i18n.t(pluralTabNameMap?.[prevActiveTab.name]);

        let tabName = `${baseValue} ${pluralName}`;

        const prevNameValue = prevPerspectiveNameValueHistory?.at(-2);

        if (prevNameValue) {
          const contextName = i18n.t(prevNameValue.name);
          const contextValue = prevNameValue.value;
          tabName = `${i18n.t(`{{valueA}} ${pluralName} of ${contextName} {{valueB}}`, { valueA: baseValue, valueB: contextValue })}`;
        }

        return tabName;
      } else {
        const tabName = i18n.t(prevActiveTab.name);
        const tabValue = prevActiveTab.value;

        return `${tabName} ${tabValue}`;
      }
    };

    return getTabName();
  }, [prevActiveTab?.name, prevActiveTab?.value, shouldPrevTabUsePluralName]);

  const actionsForScreensConfig = [
    { screens: [`${MACHINES_MAIN_SCREEN}${MachinesScreen.HISTORY}`, 'Search'], action: () => dispatch(setShouldHoverBatch(true)) },
  ];

  const shouldDisplayModalTriggerButton = currentActiveTab?.shouldDisplayModalTriggerButton;

  const shouldDisplayImnooButton = currentActiveTab?.name === DETAILS_SCREEN_TAB_NAME.Order && activeLevelSubScreenName === LevelSubScreen.GENERAL;

  const shouldDisplayDoneButton = isEditModeForPartSwitchOn;

  const isNcFileTab = currentActiveTab?.modalType === ModalType.NC_FILE;
  const shouldDisplayAddButton = isNcFileTab && ncFileData?.data?.ncFileId && ncFileData?.data?.ncFileName;

  const onBackButtonPress = () => {
    dispatch(clearHeaderConfig());
    dispatch(goBackInPerspectiveTabHistory());

    if (prevActiveTab) {
      navigateToMainDetailsScreen({
        mainScreen: prevActiveTab?.mainScreen,
        subScreen: prevActiveTab.key,
        params: prevActiveTab?.route?.params ?? {},
      });
      return;
    }
  };

  const onClose = () => {
    // Find and store the pre-close action
    let actionToExecute = null;
    for (let config of actionsForScreensConfig) {
      if (config.screens.includes(nonBottomSheetScreen?.name)) {
        actionToExecute = config.action;
        break;
      }
    }

    closeBottomSheet({ ...injectConditionalObject(!!actionToExecute, { preCloseAction: actionToExecute }) });
  };

  const onDoneEditingButtonPress = async () => {
    dispatch(setIsEditModeForPartSwitchOn(false));
    await dispatch(addProductionInformationV2(editingPartData as Record<string, unknown>));
  };

  return {
    actionsModalOpen,
    setActionsModalOpen,
    currentActiveTab,
    shouldDisplayBackButton,
    activeTabTitle,
    prevTabTitle,
    shouldDisplayModalTriggerButton,
    shouldDisplayImnooButton,
    shouldDisplayDoneButton,
    shouldDisplayAddButton,
    onBackButtonPress,
    onClose,
    onDoneEditingButtonPress,
    onUploadNewVersion,
  };
};
